import { useCallback, useMemo, useState } from 'react';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { sortBy, uniqBy } from 'lodash-es';
import { useCurrentUser } from '@hotjar/user';
import { useOrganizations, ORGANIZATIONS_QUERY_KEY } from '@hotjar/organization';
import type { Organization } from '@hotjar/organization';
import { googleAnalytics, withContext } from '@hotjar/tracking';
import { PAYMENT_ORIGINS } from '@hotjar/webapp/domains/billing/constants';
import { activateTrial, createSite } from '@hotjar/site';
import { bus, refreshData, siteCreatedEvent } from '@hotjar/webapp/utils/events';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { SITE_ACCESS_QUERY_KEY } from '@hotjar/webapp/features/Core/Nav/components/SiteDropdown/useSiteAccess';
import { useNotify } from '@hotjar/hotsauce';
import { checkout } from '@hotjar/checkout';
import { PaymentData, SitePlanInfo } from '@hotjar/webapp/billing/plan';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import type { AccountTrialInfo } from '@hotjar/account';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { USER_RESOURCES_QUERY_KEY } from '@hotjar/user-resources';
import { useTranslation } from 'react-i18next';
import { useInvalidatePermissionQuery } from '@hotjar/auth';

export interface CreateSiteForm {
  name: string;
  url: string;
  industry: string;
  org: string;
  declaration: boolean;
  priceConfirmation: boolean;
}

export const useCreateSite = (
  selectedOrgId: string,
  sitePlanInfo: { plan: FeatureGroup; planId: string } | null,
  trialInfo: AccountTrialInfo | null,
): {
  refetchPermissionsAndOrgs: () => Promise<unknown>;
  adminOrganizations: Organization[];
  selectedOrg: Organization | undefined;
  hasSubscriptionManagePermission: boolean;
  onCreate: (data: CreateSiteForm) => Promise<void>;
  isLoading: boolean;
} => {
  const { t } = useTranslation('account');
  const { data: currentUser } = useCurrentUser();
  const { data: organizations, refetch: refetchOrganizations } = useOrganizations(currentUser?.id);
  const { showToast } = useNotify();
  const [isLoading, setLoadingState] = useState(false);
  const invalidatePermissions = useInvalidatePermissionQuery();

  const { hasPermission, refetchPermissions } = useAuthorization();
  const adminOrganizations = sortBy(
    uniqBy(
      organizations?.filter((item) => hasPermission('organization', `${item.id}`, 'sites-create')),
      'id',
    ),
    'name',
  );

  const selectedOrg = useMemo(
    () => adminOrganizations.find((item) => item.id.toString() === selectedOrgId),
    [adminOrganizations, selectedOrgId],
  );

  const hasSubscriptionManagePermission = hasPermission(
    'account',
    `${selectedOrg?.accountId}`,
    'subscriptions-manage',
  );

  const selectedOrgAccount = useMemo(
    () => currentUser?.accounts?.find((item) => item.id === selectedOrg?.accountId),
    [currentUser?.accounts, selectedOrg?.accountId],
  );

  const refetchPermissionsAndOrgs = useCallback((): Promise<unknown> => {
    return refetchPermissions().then(() => refetchOrganizations());
  }, [refetchOrganizations, refetchPermissions]);

  const onCreate = (data: CreateSiteForm): Promise<void> => {
    setLoadingState(true);
    const showMessageAndRedirect = () => {
      setLoadingState(false);
      showToast({ text: t('create-site.success', { url: data.url }), kind: 'success' });
      browserHistory.push('/site/list');
    };

    return withContext({ origin: PAYMENT_ORIGINS.newSite }, async () => {
      if (selectedOrg) {
        createSite(selectedOrg.accountId, selectedOrg.id, {
          url: data.url,
          name: data.name,
          industry: data.industry,
        })
          .then(async (response) => {
            if (!response.success) {
              throw new Error();
            }

            if (sitePlanInfo?.plan === 'basic') {
              return;
            }

            if (trialInfo?.activeOrEligible) {
              await activateTrial(selectedOrg.accountId, response.siteId);
              return;
            }

            if (selectedOrgAccount && sitePlanInfo) {
              const costSpecs = [new SitePlanInfo(response.siteId, sitePlanInfo.planId)];
              const paymentData = new PaymentData(costSpecs);
              await checkout({
                account: selectedOrgAccount,
                paymentData,
              });
            }
          })
          .then(async () => {
            googleAnalytics.trackCreatedItemEvent('Added Site');
            bus.publish(siteCreatedEvent());
            await queryClient.invalidateQueries({
              predicate: (query) =>
                SITE_ACCESS_QUERY_KEY === query?.queryKey[0] ||
                ORGANIZATIONS_QUERY_KEY === query?.queryKey[0],
            });

            await queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY);
            await invalidatePermissions();

            const hasRefreshDataListener = bus.emitter.listeners(refreshData.eventType).length > 0;

            if (hasRefreshDataListener) {
              bus.publish(refreshData(showMessageAndRedirect));
            } else {
              showMessageAndRedirect();
            }
          })
          .catch(() => {
            setLoadingState(false);
            showToast({ text: 'Failed to add site.', kind: 'danger' });
          });
      }
    });
  };

  return {
    refetchPermissionsAndOrgs,
    adminOrganizations,
    selectedOrg,
    hasSubscriptionManagePermission,
    onCreate,
    isLoading,
  };
};
